import React from "react";
import {
  Hero,
  HeroContainer,
  HeroTitle,
  HeroSubTitle,
  HeroCTA,
  CTACaption,
  ImageWrapper,
  HeroHeader,
  HeroCTAWrapper,
} from "./hero-centered.styled";
import {
  HeroCenteredSliceFragment,
  PrismicReusableContent,
  PrismicReusableContentDataBodyImageGrid,
} from "../../types/graphql";
import Breadcrumbs, { BreadCrumbProps } from "../breadcrumbs/breadcrumbs";
import { PrismicRichText } from "@prismicio/react";
import { htmlSerializer } from "../../utils/htmlSerializer";
import { GatsbyImage } from "gatsby-plugin-image";
import * as FA from "../../styles/fontawesome";
import { SharedSliceType } from "../shared-slice/shared-slice";

interface IHeroCenteredProps {
  hero: HeroCenteredSliceFragment["primary"];
  items: HeroCenteredSliceFragment["items"];
  breadcrumbs?: BreadCrumbProps[];
}

enum ContentType {
  key_points = "key-points",
  quoting_steps = "quoting-steps",
  company_logos = "company-logos",
}

const getItems = (
  items: HeroCenteredSliceFragment["items"],
  type: ContentType
) => {
  const item = items.filter((item) => item.content_type === type);
  let itemDocument: PrismicReusableContent;
  let itemItems = [] as unknown as PrismicReusableContentDataBodyImageGrid;

  if (item.length === 0) return itemItems;

  itemDocument = item[0].reusable_content?.document as PrismicReusableContent;

  if (itemDocument.data.body.length === 0) return itemItems;

  const itemDocumentImageGrid = itemDocument.data.body.filter(
    (body) => body.slice_type === SharedSliceType.image_grid
  )[0] as PrismicReusableContentDataBodyImageGrid;

  if (itemDocumentImageGrid) itemItems = itemDocumentImageGrid;

  return itemItems;
};

const HeroCentered = (props: IHeroCenteredProps) => {
  const { hero, breadcrumbs, items } = props;
  const altText = "Xometry Image";
  const altImageText = hero?.image && (hero?.image.alt || altText);

  const keyPoints = items && getItems(items, ContentType.key_points);
  const quotingSteps = items && getItems(items, ContentType.quoting_steps);
  const companyLogos = items && getItems(items, ContentType.company_logos);

  return !hero ? (
    <></>
  ) : (
    <Hero>
      {breadcrumbs && breadcrumbs.length > 1 && (
        <Breadcrumbs breadcrumbs={breadcrumbs} />
      )}
      <HeroContainer>
        <HeroHeader>
          {hero.title && <HeroTitle>{hero.title}</HeroTitle>}
          {hero.subtitle && hero.subtitle.richText && (
            <HeroSubTitle>
              <PrismicRichText
                field={hero.subtitle.richText}
                components={htmlSerializer}
              />
            </HeroSubTitle>
          )}
        </HeroHeader>
        {keyPoints && keyPoints.items && (
          <ul className={keyPoints.primary?.variant || ""}>
            {keyPoints.items?.map((item, index) => (
              <li key={index}>
                <img
                  src={item.image?.url as string}
                  alt={item.image?.alt || altText}
                />
                <span>{item.title}</span>
              </li>
            ))}
          </ul>
        )}
        {hero.cta_link && hero.cta_text && (
          <HeroCTAWrapper>
            <HeroCTA
              kind="primary"
              scale="xlarge"
              pageSection="centered-hero"
              ctaText={hero.cta_text}
              linkURL={hero.cta_link && hero.cta_link.url}
              target={hero.cta_link && hero.cta_link.target}
            />
            {hero.cta_microcopy && (
              <CTACaption>
                <i css={FA.LockKeyholeSolid}></i>&nbsp;&nbsp;All uploads are
                secure and confidential.
              </CTACaption>
            )}
          </HeroCTAWrapper>
        )}
        {hero?.image?.gatsbyImageData && (
          <ImageWrapper>
            <GatsbyImage
              image={hero.image.gatsbyImageData}
              alt={altImageText || "Xometry Image"}
              loading="eager"
            />
          </ImageWrapper>
        )}
        {quotingSteps && quotingSteps.items && (
          <ul className={quotingSteps.primary?.variant || ""}>
            {quotingSteps.items?.map((item, index) => (
              <li key={index}>
                <GatsbyImage
                  key={index}
                  image={item.image?.gatsbyImageData}
                  alt={item.image?.alt || "Xometry Image"}
                  loading="eager"
                />
                <h6>{item.title}</h6>
              </li>
            ))}
          </ul>
        )}
        {companyLogos && companyLogos.items && (
          <div className={companyLogos.primary?.variant || ""}>
            <ul>
              {companyLogos.items?.map((item, index) => (
                <li key={index}>
                  <img
                    src={item.image?.url as string}
                    alt={item.image?.alt || altText}
                  />
                </li>
              ))}
            </ul>
            <h6>{companyLogos.primary?.title}</h6>
          </div>
        )}
      </HeroContainer>
    </Hero>
  );
};

export default HeroCentered;
